import { createFileRoute, Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { motion } from 'framer-motion';
import { ShieldCheck, ThumbsUp, Shield, Award } from 'lucide-react';
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/sobre-nos")({
  component: AboutPage,
});

function AboutPage() {
  const steps = [
    { title: 'Contato', desc: 'Você entra em contato com a nossa equipe.' },
    { title: 'Entendimento', desc: 'Analisamos sua necessidade, ambiente e tipo de ruído.' },
    { title: 'Proposta', desc: 'Criamos uma solução personalizada para o seu espaço.' },
    { title: 'Fabricação', desc: 'Produzimos as esquadrias sob medida com materiais de alta qualidade.' },
    { title: 'Instalação', desc: 'Realizamos a instalação de forma rápida, limpa e eficiente.' },
  ];

  return (
    <>
      <SEO 
        title="Sobre a STOP SOM | Janelas e esquadrias acústicas no Rio de Janeiro"
        description="Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ."
      />
      <div className="bg-white">
      {/* Hero / Institucional */}
      <section className="pt-32 lg:pt-48 pb-12 lg:pb-24 px-6 overflow-hidden">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="z-10 text-center lg:text-left"
            >
               <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-primary mb-8 leading-tight">
                Soluções em Janelas Acústicas de Alto Desempenho
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 font-medium mb-6 leading-relaxed">
                Líder em isolamento acústico para residências, apartamentos e empresas em todo o Rio de Janeiro.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                A STOP SOM Janelas Acústicas é referência em soluções de isolamento para quem busca silêncio, conforto e bem-estar. Com fábrica localizada em Duque de Caxias - RJ, atendemos clientes em todo o estado do Rio de Janeiro.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                Nossa expertise abrange janelas acústicas, esquadrias e sistemas antirruído de alto desempenho, projetados meticulosamente para atender às demandas de apartamentos, residências, escritórios e consultórios. Garantimos vedação absoluta e acabamento impecável em cada projeto.
              </p>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                {['Confiança', 'Qualidade', 'Projeto sob medida'].map((tag) => (
                  <div key={tag} className="border border-secondary text-primary bg-secondary/5 rounded-full px-5 py-2 font-semibold shadow-sm">
                    {tag}
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-accent/10 rounded-[2.5rem] rotate-3 transform" />
              <img 
                src="/institucional-sobre-nos.webp" 
                alt="Conforto acústico Stop Som" 
                width="800"
                height="600"
                className="relative rounded-[2.5rem] shadow-2xl w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover object-center border-4 border-white"
              />
              <div className="absolute -bottom-6 -right-6 bg-accent p-6 md:p-10 rounded-[2.5rem] shadow-2xl border-4 border-white z-20">
                <div className="flex flex-col items-center justify-center text-center">
                  <p className="text-primary font-bold text-lg md:text-xl leading-tight">Atendimento em todo o</p>
                  <p className="text-primary font-black text-2xl md:text-3xl uppercase tracking-tighter">Rio de Janeiro</p>
                  <div className="h-1 w-12 bg-primary/20 mt-2 rounded-full" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-12 md:py-24 bg-gray-50 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-foreground mb-4">Por que nos escolher?</h2>
            <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <ShieldCheck />, title: "Confiança", desc: "Compromisso com prazos e resultados garantidos." },
              { icon: <ThumbsUp />, title: "Qualidade", desc: "Materiais de alta performance e acabamento premium." },
              { icon: <Shield />, title: "Projeto Sob Medida", desc: "Soluções personalizadas para cada necessidade." },
              { icon: <Award />, title: "Expertise", desc: "Equipe técnica altamente qualificada." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all text-center">
                <div className="text-secondary mb-6 flex justify-center">{item.icon}</div>
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como trabalhamos */}
      <section className="py-12 md:py-24 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-foreground mb-4">Como trabalhamos</h2>
            <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2" />
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-12 relative z-10">
              {steps.map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center group">
                  <div className="w-16 h-16 rounded-2xl bg-white border-2 border-primary text-primary flex items-center justify-center font-bold text-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all transform group-hover:-translate-y-2 group-hover:shadow-xl group-hover:shadow-primary/20">
                    {i + 1}
                  </div>
                  <h3 className="font-semibold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-500 font-normal text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 md:py-24 px-6 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="bg-primary rounded-[3rem] p-12 lg:p-24 relative overflow-hidden text-center text-white">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/20 skew-x-12 translate-x-1/2" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold mb-8">Pronto para recuperar o silêncio?</h2>
              <p className="text-xl opacity-80 font-normal mb-12 max-w-2xl mx-auto leading-relaxed">Fale com a STOP SOM e receba uma solução personalizada para o seu espaço.</p>
              <a 
                href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("Solicitar orçamento", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "cta-final")}
                data-gtm-event="whatsapp_click"
                data-conversion="whatsapp"
                data-button-location="cta-final"
                className="inline-flex items-center gap-3 bg-accent text-primary px-12 py-6 rounded-2xl font-bold text-xl hover:bg-white transition-all transform hover:scale-105 shadow-2xl shadow-black/20"
              >
                <WhatsAppIcon className="w-8 h-8" /> Solicitar orçamento
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "STOP SOM Janelas Acústicas",
          "image": "https://stopsomesquadrias.com.br/logo.png",
          "@id": "https://stopsomesquadrias.com.br",
          "url": "https://stopsomesquadrias.com.br",
          "telephone": "+55 21 99269-8354",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua Itatinga, 511",
            "addressLocality": "Rio de Janeiro",
            "addressRegion": "RJ",
            "postalCode": "25000-000",
            "addressCountry": "BR"
          },
          "areaServed": {
            "@type": "State",
            "name": "Rio de Janeiro"
          },
          "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ."
        })}
      </script>
    </div>
    </>
  );
}